import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Typography, Chip, Stack, Divider, Avatar, CircularProgress,
} from '@mui/material'
import {
  Warning as WarningIcon,
  Block as BlockIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { DuplicateCheckResult, DuplicateMatch, DuplicateMatchReason } from '../../lib/api/customersApi'
import { themeColors } from '../../app/layout/theme'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

function reasonLabel(reason: DuplicateMatchReason): string {
  switch (reason) {
    case 'ExactName':  return 'Same Name'
    case 'ExactPhone': return 'Same Phone'
    case 'ExactEmail': return 'Same Email'
    case 'FuzzyName':  return 'Similar Name'
  }
}

function reasonColor(reason: DuplicateMatchReason): 'error' | 'warning' {
  return reason === 'FuzzyName' ? 'warning' : 'error'
}

// ── Match Card ────────────────────────────────────────────────────────────────

function MatchCard({ match, onNavigate }: { match: DuplicateMatch; onNavigate: (id: string) => void }) {
  const initials = getInitials(match.companyName || match.name)
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
      border: '1px solid #e5e7eb', borderRadius: '10px', bgcolor: '#fafafa',
    }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: themeColors.primary, fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontSize="13px" fontWeight={700} noWrap>
          {match.companyName || match.name}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
          {match.phone && (
            <Typography fontSize="11px" color="text.secondary">{match.phone}</Typography>
          )}
          {match.email && (
            <Typography fontSize="11px" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
              {match.email}
            </Typography>
          )}
          {match.city && (
            <Typography fontSize="11px" color="text.secondary">{match.city}</Typography>
          )}
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Chip
          label={reasonLabel(match.matchReason)}
          color={reasonColor(match.matchReason)}
          size="small"
          sx={{ fontSize: '10px', height: 20, fontWeight: 600 }}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
          onClick={() => onNavigate(match.id)}
          sx={{ fontSize: '11px', px: 1, py: 0.25, minWidth: 'auto', borderRadius: '6px',
            textTransform: 'none', borderColor: '#e5e7eb', color: '#374151', height: 28 }}
        >
          View
        </Button>
      </Box>
    </Box>
  )
}

// ── Main Modal ────────────────────────────────────────────────────────────────

interface DuplicateWarningModalProps {
  open: boolean
  result: DuplicateCheckResult | null
  isChecking: boolean
  onClose: () => void
  /** Called when the user explicitly chooses to create despite fuzzy warnings. */
  onCreateAnyway: () => void
}

export function DuplicateWarningModal({
  open,
  result,
  isChecking,
  onClose,
  onCreateAnyway,
}: DuplicateWarningModalProps) {
  const navigate = useNavigate()

  const isBlocked = result?.hasExactMatch ?? false
  const hasFuzzy  = result?.hasFuzzyMatches ?? false
  const allMatches = [
    ...(result?.exactMatches ?? []),
    ...(result?.fuzzyMatches ?? []),
  ]

  const handleNavigate = (id: string) => {
    onClose()
    navigate(`/customers/${id}`)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            bgcolor: isBlocked ? '#fee2e2' : '#fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isBlocked
              ? <BlockIcon sx={{ fontSize: 20, color: '#dc2626' }} />
              : <WarningIcon sx={{ fontSize: 20, color: '#d97706' }} />}
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize="16px" color="text.primary">
              {isBlocked ? 'Duplicate Company Found' : 'Similar Companies Found'}
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              {isBlocked
                ? 'This company already exists in your organisation.'
                : 'We found companies that look similar. Review before creating.'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {isChecking ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {allMatches.map(match => (
              <MatchCard key={match.id} match={match} onNavigate={handleNavigate} />
            ))}
          </Stack>
        )}

        {isBlocked && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
            <Typography fontSize="12.5px" color="#b91c1c" fontWeight={500}>
              Creation blocked — an exact match already exists. Open the existing record to update it,
              or contact your admin if this is a genuine new company.
            </Typography>
          </Box>
        )}

        {!isBlocked && hasFuzzy && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
            <Typography fontSize="12.5px" color="#92400e" fontWeight={500}>
              These may be the same company under a slightly different name. You can still create a new
              record, but we recommend reviewing the existing ones first.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small"
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#e5e7eb', color: '#374151' }}>
          Cancel
        </Button>
        {!isBlocked && (
          <Button onClick={onCreateAnyway} variant="contained" size="small"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600,
              bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
            Create Anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
